export type AnswersRerunMode = 'resume' | 'retryGaps' | 'fresh';

export type SupervisorAnswerRunStatus = 'active' | 'completed' | 'partial' | 'failed';

export interface SupervisorAnswerTaskProgress {
  label: string;
  key: string;
  kind: string;
  questionId: string;
  order: number | null;
  parent: string;
  status: string;
  winner: string;
  attempt: number | null;
  verdict: string;
  quality: number | null;
  answerStatus: string;
  facts: number | null;
  citations: number | null;
  wallSeconds: number | null;
  agent: string;
  phase: string;
  elapsedSeconds: number | null;
  deadlineSeconds: number | null;
}

export interface SupervisorAnswerRunTotals {
  answered: number;
  partial: number;
  unanswerable: number;
  failed: number;
}

export interface SupervisorAnswerRunState {
  runId: string;
  engagementId: string;
  resumed: boolean;
  rerunMode: AnswersRerunMode;
  recoveredAnswers: number;
  trackingPath: string;
  answersPath: string;
  status: SupervisorAnswerRunStatus;
  tasks: readonly SupervisorAnswerTaskProgress[];
  totals: SupervisorAnswerRunTotals;
  seconds: number | null;
}

const EMPTY_TOTALS: SupervisorAnswerRunTotals = {
  answered: 0,
  partial: 0,
  unanswerable: 0,
  failed: 0,
};

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

const stringValue = (value: unknown): string => (typeof value === 'string' ? value : '');
const numberValue = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

const isTerminalTaskStatus = (status: string): boolean =>
  ['completed', 'answered', 'partial', 'failed', 'unanswerable', 'skipped'].includes(status);

const taskFromRecord = (
  data: Record<string, unknown>,
  fallbackLabel = ''
): SupervisorAnswerTaskProgress | null => {
  const label = stringValue(data.label) || fallbackLabel;
  if (!label) {
    return null;
  }
  return {
    label,
    key: stringValue(data.key),
    kind: stringValue(data.kind),
    questionId: stringValue(data.questionId),
    order: numberValue(data.order),
    parent: stringValue(data.parent),
    status: stringValue(data.status),
    winner: stringValue(data.winner),
    attempt: numberValue(data.attempt),
    verdict: stringValue(data.verdict),
    quality: numberValue(data.quality),
    answerStatus: stringValue(data.answerStatus),
    facts: numberValue(data.facts),
    citations: numberValue(data.citations),
    wallSeconds: numberValue(data.wallSeconds),
    agent: '',
    phase: '',
    elapsedSeconds: null,
    deadlineSeconds: null,
  };
};

const tasksFromGraph = (graph: unknown): SupervisorAnswerTaskProgress[] => {
  if (Array.isArray(graph)) {
    return graph.flatMap(item => {
      const record = asRecord(item);
      const task = record ? taskFromRecord(record) : null;
      return task ? [task] : [];
    });
  }
  const record = asRecord(graph);
  if (!record) {
    return [];
  }
  const values = Array.isArray(record.items) ? record.items : null;
  if (values) {
    return tasksFromGraph(values);
  }
  return Object.entries(record).flatMap(([label, value]) => {
    const item = asRecord(value);
    const task = item ? taskFromRecord(item, label) : null;
    return task ? [task] : [];
  });
};

const taskStatusForEvent = (event: string, payloadStatus: string): string => {
  if (payloadStatus) return payloadStatus;
  switch (event) {
    case 'answerTaskStarted':
      return 'started';
    case 'answerTaskRetrying':
      return 'retrying';
    case 'answerVerifying':
      return 'verifying';
    case 'answerTaskCompleted':
      return 'completed';
    case 'answerTaskFailed':
      return 'failed';
    case 'answerTaskUnanswerable':
      return 'unanswerable';
    case 'answerTaskSkipped':
      return 'skipped';
    default:
      return '';
  }
};

const mergeTaskTransition = (
  previous: SupervisorAnswerRunState,
  event: string,
  data: Record<string, unknown>
): SupervisorAnswerRunState => {
  const incoming = taskFromRecord(data);
  if (!incoming) return previous;
  const index = previous.tasks.findIndex(task => task.label === incoming.label);
  if (index === -1 && event !== 'answerTaskStarted') {
    return previous;
  }
  const existing = index >= 0 ? previous.tasks[index] : null;
  const status = taskStatusForEvent(event, incoming.status);
  const merged: SupervisorAnswerTaskProgress = {
    ...(existing ?? incoming),
    ...Object.fromEntries(
      Object.entries(incoming).filter(([, value]) => value !== '' && value !== null)
    ),
    status,
    phase: isTerminalTaskStatus(status) ? '' : existing?.phase ?? '',
    agent: isTerminalTaskStatus(status) ? '' : existing?.agent ?? '',
    elapsedSeconds: isTerminalTaskStatus(status) ? null : existing?.elapsedSeconds ?? null,
    deadlineSeconds: isTerminalTaskStatus(status) ? null : existing?.deadlineSeconds ?? null,
  } as SupervisorAnswerTaskProgress;
  const tasks =
    index === -1
      ? [...previous.tasks, merged]
      : previous.tasks.map((task, taskIndex) => (taskIndex === index ? merged : task));
  return { ...previous, tasks };
};

const mergeHeartbeat = (
  previous: SupervisorAnswerRunState,
  data: Record<string, unknown>
): SupervisorAnswerRunState => {
  const label = stringValue(data.label);
  const index = previous.tasks.findIndex(task => task.label === label);
  if (index < 0 || isTerminalTaskStatus(previous.tasks[index].status)) {
    return previous;
  }
  const task = previous.tasks[index];
  const tasks = previous.tasks.map((candidate, taskIndex) =>
    taskIndex === index
      ? {
          ...task,
          agent: stringValue(data.agent) || task.agent,
          phase: stringValue(data.phase) || task.phase,
          elapsedSeconds: numberValue(data.elapsedS) ?? task.elapsedSeconds,
          deadlineSeconds: numberValue(data.deadlineS) ?? task.deadlineSeconds,
        }
      : candidate
  );
  return { ...previous, tasks };
};

const parseEventData = (data: string): Record<string, unknown> | null => {
  try {
    return asRecord(JSON.parse(data) as unknown);
  } catch {
    return null;
  }
};

const TASK_EVENTS = new Set([
  'answerTaskStarted',
  'answerTaskRetrying',
  'answerVerifying',
  'answerTaskCompleted',
  'answerTaskFailed',
  'answerTaskUnanswerable',
  'answerTaskSkipped',
]);

/** Non-throwing S-06 event reducer. Unknown names and malformed payloads are inert. */
export function reduceSupervisorAnswerEvent(
  previous: SupervisorAnswerRunState | null,
  event: string,
  data: string
): SupervisorAnswerRunState | null {
  const payload = parseEventData(data);
  if (!payload) return previous;

  if (event === 'answersStarted') {
    const runId = stringValue(payload.runId);
    if (!runId) return previous;
    const rerunMode = stringValue(payload.rerunMode);
    return {
      runId,
      engagementId: stringValue(payload.engagementId),
      resumed: payload.resumed === true,
      rerunMode: rerunMode === 'fresh' || rerunMode === 'retryGaps' ? rerunMode : 'resume',
      recoveredAnswers: numberValue(payload.recoveredAnswers) ?? 0,
      trackingPath: stringValue(payload.trackingPath),
      answersPath: stringValue(payload.answersPath),
      status: 'active',
      tasks: tasksFromGraph(payload.graph),
      totals: { ...EMPTY_TOTALS },
      seconds: null,
    };
  }
  if (!previous) return previous;

  if (TASK_EVENTS.has(event)) {
    return mergeTaskTransition(previous, event, payload);
  }
  if (event === 'answerProgress') {
    return mergeHeartbeat(previous, payload);
  }
  if (event === 'answersCompleted') {
    const status = stringValue(payload.status);
    return {
      ...previous,
      status: status === 'partial' ? 'partial' : 'completed',
      totals: {
        answered: numberValue(payload.answered) ?? 0,
        partial: numberValue(payload.partial) ?? 0,
        unanswerable: numberValue(payload.unanswerable) ?? 0,
        failed: numberValue(payload.failed) ?? 0,
      },
      seconds: numberValue(payload.seconds),
      tasks: previous.tasks.map(task => ({
        ...task,
        phase: '',
        agent: '',
        elapsedSeconds: null,
        deadlineSeconds: null,
      })),
    };
  }
  if (event === 'answersFailed') {
    return {
      ...previous,
      status: 'failed',
      tasks: previous.tasks.map(task => ({
        ...task,
        phase: '',
        agent: '',
        elapsedSeconds: null,
        deadlineSeconds: null,
      })),
    };
  }
  return previous;
}
